import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';
// import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import {
    toNumberOrNull, 
    hasValueOrZero, 
    // toLuxonDateTime,
} from '@/js/filters-helpers.js';
import {formatMoney, extractCurrencyAmountFromString} from '@/js/utils/moneyUtils.js';
import { 
    // parseDecimal, 
    parseFixedDecimal,
} from '@/js/utils/numberUtils.js';
import { toLuxonDate } from '@/js/utils/dtUtils.js';
import { DateTime } from 'luxon';
import { isSuperset } from '@/js/utils/setUtils.js';

// HELPERS

const trimString = (val) => {
    if (isNil(val)) {
        return val;
    }
    if (!(val instanceof String) && !isString(val)) {
        return val;  // don't try to trim non-strings
    }
    return val.trim();
};

// DATE HELPERS

const toJSDate = (rawVal) => {
  // ag-grid date filters want JS Date objects
  if (!rawVal) {
    return null;
  }
  const luxonDateTime = toLuxonDate(rawVal);
  return luxonDateTime?.isValid ? luxonDateTime.toJSDate() : null;
};

const toISODate = (rawVal) => {
  // convert to ISO date string (YYYY-MM-DD) - for passing to backend
  if (!rawVal) {
    return '';
  }
  const luxonDateTime = toLuxonDate(rawVal);
  return luxonDateTime?.isValid ? luxonDateTime.toISODate() : '';
};

const formatDate = (dateVal) => {  // might be JS Date, or ISO string, or null/undefined
  if (!dateVal) {
    return '';
  }
  const luxonDateTime = toLuxonDate(dateVal);
  return luxonDateTime?.isValid ? luxonDateTime.toLocaleString(DateTime.DATE_SHORT) : '';
};

// const formatDateTime = (jsDate) => {
//   if (!jsDate) {
//     return '';
//   }
//   const luxonDateTime = DateTime.fromJSDate(jsDate);
//   return luxonDateTime.isValid ? luxonDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '';
// };

const getDecimalPlacesFromSchema = (colSchema) => {
    // returns null if no specific decimal places -- might have to look other places too... like in 'props' or 'validation_rules'???
    const pathsToCheck = [  // paths relative to colSchema
        'props.decimalPlaces',  // will likely be here....
        'props.decimal_places',
        'decimalPlaces',
        'decimal_places',
    ]
    for (const path of pathsToCheck) {
        const val = get(colSchema, path);
        if (isNumber(val)) {
            return val;
        }
    }
    return null;
};
const getCurrencyOptionsFromSchema = (colSchema) => {
    // returns array of currency codes, including only one if its fixed currency, possibly from tenantInfo if not specified in schema
    const fixedCurrencyPaths = [  // paths relative to colSchema
        'props.fixedCurrency',  // will likely be here....
        'props.fixed_currency',
        'props.currency',
        'fixedCurrency',
        'fixed_currency',
    ];
    for (const path of fixedCurrencyPaths) {
        const val = get(colSchema, path);
        if (isString(val)) {
            return [val];
        }
    }
    const currencyOptionsPaths = [
        'props.currencyOptions', 
        'props.currency_options',
        'props.currencyChoices',
        'props.currency_choices',
        'currencyOptions',
        'currency_options',
    ];
    for (const path of currencyOptionsPaths) {
        const val = get(colSchema, path);
        if (Array.isArray(val)) {
            return [...val].sort();  // return sorted copy
        }
    }
    // still nothing?  then we get from tenantInfo if available
    const currencyCodes = window.tenantInfo?.settings?.currency_settings?.currencyChoices?.map(c => c.value) ?? [];
    return currencyCodes.sort();  // return a sorted copy
};

const getErrorsFromRules = (rules, value) => {
    return rules.map(([rule, errorMessage]) => {
        return !rule(value) ? errorMessage : null;
    }).filter(Boolean);
};
// or...
// return (rules, value) => {
//     const errors = [];
//     for (const [rule, errorMessage] of rules) {
//         if (!rule(value)) {
//             errors.push(errorMessage);
//         }
//     }
//     return errors;
// };

/** 
 * Wraps a value formatter function (that only takes the value) for use with ag-Grid valueFormatter (which takes params). 
*/
const makeAgGridFormatter = (valueFormatterFunc) => {
    return (params) => {
        return valueFormatterFunc(params.value);
    }
};

/**
 * Wraps a value parser function (that only takes the value) for use with ag-Grid valueParser (which takes params).
 * @param {*} valueParserFunc
 * @returns {function(*): *}
 */
const makeAgGridParser = (valueParserFunc) => {
    return (params) => {
        return valueParserFunc(params.newValue);
    }
};

// COL TYPES (my own thing, not ag-grid thing)
// TODO - these should be classes (different subclasses of same base), instantiated with colSchema
// each should have methods to get formatter, parser, validator/rules, and maybe base colDef

const hiddenType = {
    typeKey: 'hidden',
    label: 'Hidden',
    // defaultValue: null,  // ???
    getFormatter: () => {
        return (val) => val;  // always return value unchanged?  or empty string or null?
    },
    getParser: () => {
        return (val) => val;  // always return value unchanged?  or empty string or null?
    },
    getRules: () => {
        return [];  // no validation rules
    },
    getBaseColDef: function (colSchema) {
        const defaults = {
            hide: true,  // any other special things?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        return colDef;
    },
};

const textType = {
    typeKey: 'text',
    label: 'Text',
    defaultValue: '',
    getFormatter: () => {
        return (value) => value;  // no special formatting
    },
    getParser: () => {
        // trim the value?
        return (value) => {
            // there is a bug specifically with the agLargeTextCellEditor where it returns undefined if you open the editor and close it without changing anything
            // to account for that, and to account for just keeping things consistent, we will coerce undefined and null to empty string ''
            // but NOTE: it seems parser doesn't always get called / honored from the agLargeTextCellEditor (it trims its own strings, then still sets things as undefined), so we also have to handle that in our valueSetter???
            // TODO - maybe just make our own large text editor that works better???  gotta make sure you understand why it happens... might also happen with other provided editors?
            // console.log('textType parser got value:', value);
            if (isNil(value)) {
                return '';
            }
            return trimString(value);
        };
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        if (colSchema.min_length) {
            rules.push([(value) => value.length >= colSchema.min_length, `Min length is ${colSchema.min_length}`]);
        }
        if (colSchema.max_length) {
            rules.push([(value) => value.length <= colSchema.max_length, `Max length is ${colSchema.max_length}`]);
        }
        if (colSchema.pattern) {
            const regex = new RegExp(colSchema.pattern);
            rules.push([(value) => regex.test(value), `Value does not match required pattern`]);
        }
        if (colSchema.required) {
            rules.push([(value) => !isNil(value) && value !== '', `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const isTextArea = colSchema.rendererKey === 'textarea';
        const defaults = {
            cellEditor: isTextArea ? 'customTextAreaEditor' : 'agTextCellEditor',
            filter: 'agTextColumnFilter',
            sortable: true,
            // other defaults?
            // flex: isTextArea ? 2 : undefined, // flex is not very useful for us since we update widths via API methods so much
            wrapText: isTextArea,  // enable text wrapping for textarea renderer -- gotta set different line height too, right?  or custom renderer?
            autoHeight: isTextArea,  // should make row height auto-adjust to text for textarea renderer
        };
        // OUR OWN CUSTOM RENDERER FOR TEXTAREA, because of bugs with agLargeTextCellEditor and agTextCellEditor for textareas
        if (isTextArea) {
            defaults['cellEditorParams'] = { maxLength: colSchema.max_length || undefined, rows: 4 };
            defaults['cellStyle'] = {whiteSpace: 'pre-line'};  // NOTE:  ag-row has things that affect this also...
        }
        const colDef = {...defaults, ...colSchema.getColDefData()};
        colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));

        // if (colSchema.rendererKey === 'textarea') {
        //     colDef['cellEditor'] = 'agLargeTextCellEditor';
        //     colDef['cellEditorParams'] = { maxLength: colSchema.max_length || 1000, rows: 4, cols: 50 };
        //     // NOTE: there is a bug? with agLargeTextCellEditor where it loses the first character you type... it goes into edit mode, but first char is lost.
        //     //       charPress gets passed in params, but not used by agLargeTextCellEditor internally???  need to make our own editor that handles this properly???
        //     // NOTE: there seems to be a bug / strange behavior ONLY with the agLargeTextCellEditor
        //     // actually, also happens with agTextCellEditor too!
        //     // where if you open the editor, then close it without changing anything, the cell value is set to undefined, EVEN IF IT WAS EMPTY STRING BEFORE
        //     // but if you type something, then delete it all, it goes to empty string as expected (if it was touched)
        //     // that is wrong in my book... so we will have to handle that in our valueParser or something... just always coerce undefined to '', maybe only if old value was ''?

        //     // colDef['autoHeight'] = true;  // should make row height auto-adjust to text?
        //     // const currentMinWidth = colDef['minWidth'] || 0;
        //     // if (currentMinWidth < 200) {
        //     //     colDef['minWidth'] = 200;
        //     // }
        // }
        return colDef;
    },
};

const booleanType = {  // TODO - specify true/false labels, and whether null is allowed?  (or just use required for that?) - refer to our multiple checkbox renderers in formulate!
    typeKey: 'boolean',
    label: 'Boolean',
    defaultValue: undefined,  // this should tell our machinery that there is NO default value
    getFormatter: (colSchema) => {
        // TODO - get labels from backend colSchema - this is just a placeholder for now...
        const trueLabel = colSchema.boolean_true_label || 'Yes';
        const falseLabel = colSchema.boolean_false_label || 'No';
        return (value) => {
            if (isNil(value)) {
                return '';
            }
            return value ? trueLabel : falseLabel;
        };
    },
    getParser: () => {
        // trim the value?
        return (value) => {
            const trueVals = ['true', 'True', '1', 'yes', 'on', true, 1];
            const falseVals = ['false', 'False', '0', 'no', 'off', false, 0];
            if (isNil(value) || value === '') {
                return null;  // allow null, and coerce empty string to null
            }
            if (trueVals.includes(value)) {
                return true;
            }
            if (falseVals.includes(value)) {
                return false;
            }
            return null;  // if not matched, return null
        };
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        // TODO - must allow for required and non-required ones.... refer to our different checkbox renderers in formulate
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        if (colSchema.required) {
            rules.push([(value) => !isNil(value) && value !== '', `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const defaults = {
            cellEditor: 'booleanCellEditor',
            cellEditorParams: {
                trueLabel: colSchema.boolean_true_label || 'Yes',
                falseLabel: colSchema.boolean_false_label || 'No',
            },
            sortable: true,
            headerClass: ['column-align-center'],
            cellClass: ['column-align-center'],
            cellClassRules: {
                'text-success': (params) => params.value === true,
                'text-danger': (params) => params.value === false,
            },
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));
        return colDef;
    },
};


const integerType = {
    typeKey: 'integer',
    label: 'Integer',
    defaultValue: null,
    getFormatter: () => {
        return (value) => value;  // no special formatting
    },
    getParser: () => {
        // trim the value?
        return (value) => toNumberOrNull(trimString(value));
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        if (colSchema.min) {
            rules.push([(value) => value >= colSchema.min, `Min value is ${colSchema.min}`]);
        }
        if (colSchema.max) {
            rules.push([(value) => value <= colSchema.max, `Max value is ${colSchema.max}`]);
        }
        if (colSchema.required) {
            rules.push([(value) => hasValueOrZero(value), `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const defaults = {
            cellEditor: 'agTextCellEditor',
            filter: 'agNumberColumnFilter',
            filterParams: {
                allowedCharPattern: '\\d\\-\\,', // note: ensure you escape as if you were creating a RegExp from a string
                numberParser: (textVal) => {
                    return textVal == null ? null : parseFloat(textVal.replace(',', '.'));
                },
            },
            sortable: true,
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // add/override some things based on colSchema?
        colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));
        return colDef;
    },
};


const decimalType = {
    typeKey: 'decimal',
    label: 'Decimal',
    defaultValue: null,
    // getDecimalPlaces: (colSchema) => {
    //     // returns null if no specific decimal places
    //     return isNumber(colSchema.decimal_places) ? colSchema.decimal_places : null;
    // },
    getFormatter: (colSchema) => {
        const decimalPlaces = getDecimalPlacesFromSchema(colSchema);
        return (value) => hasValueOrZero(value) ? (isNil(decimalPlaces) ? value.toString() : value.toFixed(decimalPlaces)) : '';
    },
    getParser: (colSchema) => {
        const decimalPlaces = getDecimalPlacesFromSchema(colSchema) ?? 2;
        return (value) => {
            const val = trimString(value);
            // TODO - need to make it consistent for with/without decimalPlaces????
            // return isNil(decimalPlaces) ? parseDecimal(val) : parseFixedDecimal(val, decimalPlaces);
            return parseFixedDecimal(val, decimalPlaces);  // always parse to fixed decimal places for decimals
        };

    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        if (colSchema.min) {
            rules.push([(value) => value >= colSchema.min, `Min value is ${colSchema.min}`]);
        }
        if (colSchema.max) {
            rules.push([(value) => value <= colSchema.max, `Max value is ${colSchema.max}`]);
        }
        if (colSchema.required) {
            rules.push([(value) => hasValueOrZero(value), `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const defaults = {
            cellEditor: 'agTextCellEditor',
            filter: 'agNumberColumnFilter',
            filterParams: {
                allowedCharPattern: '\\d\\-\\,', // ??? - ensure you escape as if you were creating a RegExp from a string
                numberParser: (textVal) => {
                    return textVal == null ? null : parseFloat(textVal.replace(',', '.'));
                },
            },
            sortable: true,
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // add/override some things based on colSchema?
        colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));
        return colDef;
    },
};


const moneyType = {  // we should be able to use this with fixedCurrency or multiCurrency?
    typeKey: 'money',
    label: 'Money',
    defaultValue: null,
    // getDecimalPlaces: (colSchema) => {
    //     // returns null if no specific decimal places
    //     return isNumber(colSchema.decimal_places) ? colSchema.decimal_places : null;
    // },
    // getCurrencyOptions: (colSchema) => {
    //     // returns array of currency codes, or null if none specified
    //     // get form tenantInfo???
    //     if (colSchema.fixed_currency || colSchema.currency) {
    //         return [colSchema.fixed_currency || colSchema.currency];  // return single currency in array
    //     }
    //     let currencyCodes = Array.isArray(colSchema.currency_options) ? colSchema.currency_options : null;
    //     if (!currencyCodes || currencyCodes.length === 0) {
    //         currencyCodes = window.tenantInfo?.settings?.currency_settings?.currencyChoices?.map(c => c.value) ?? [];
    //     }
    //     return currencyCodes;
    // },
    getFormatter: (colSchema) => {
        // NOTE: we use this for formatting for excel, etc.. but for ag-grid we will use custom formatter that uses similar things
        // because we want to still show amounts without currency selected -- but highlight missing currency in red
        const decimalPlaces = getDecimalPlacesFromSchema(colSchema);
        return (value) => {
            // console.log('running money formatter on value:', value, 'decimalPlaces:', decimalPlaces);
            const { amount, currency } = value || {};
            if (!hasValueOrZero(amount) || !currency) {
                return '';
            }
            return formatMoney(amount, currency, decimalPlaces);
        };
    },
    getParser: (colSchema) => {
        const decimalPlaces = getDecimalPlacesFromSchema(colSchema) ?? 2;
        const currencyCodeOptions = getCurrencyOptionsFromSchema(colSchema);
        const singleCurrencyOrNull = currencyCodeOptions.length === 1 ? currencyCodeOptions[0] : null;  // if only one option, use that as default
        return (value) => {
            // TODO - must also try parsing formatted strings such as "12.34 USD" or "$12.34" ???? -- because we will export things that way... look at how excel handles these things too...
            let initialAmount = null;
            let initialCurrency = null;
            if (!value) {  // totally empty?
                initialAmount = null;
                initialCurrency = singleCurrencyOrNull;  // will be null if we don't have exactly one option for currency
            } else if (typeof value === 'string') {
                // try to extract currency and amount from string, like "$12.34" or "12.34 USD" or "USD 12.34"
                // this will always return a 2-element array, possibly with nulls in both places, or null in one or other
                const [parsedCurrency, parsedAmountNumber] = extractCurrencyAmountFromString(value);
                // we still have to parse the amount to fixed decimal places, if we got one  (for consistency with rest of this parser)
                initialAmount = isNumber(parsedAmountNumber) ? parseFixedDecimal(parsedAmountNumber, decimalPlaces) : null;
                initialCurrency = parsedCurrency || singleCurrencyOrNull;  // use the result of extraction (if we got one) or the singleCurrency if we have one
            } else if (typeof value === 'number') {  // just got a string or number?  like in a paste operation
                initialAmount = parseFixedDecimal(value, decimalPlaces);
                initialCurrency = singleCurrencyOrNull;  // will be null if we don't have exactly one option for currency
            } else if (typeof value === 'object' && value !== null) {
                // if its an object, try to get amount and currency from it.. those take priority, but for currency, set it to the singleCurrency if we have one
                initialAmount = value.amount ? parseFixedDecimal(value.amount, decimalPlaces) : null;
                initialCurrency = value.currency || singleCurrencyOrNull;  // will be null if we don't have exactly one option
            }
            // now check if we have valid combination.... if not, return null, otherwise, return object
            // or just always return object, with amount possibly null, currency possibly null... allow validation to catch it, maybe this is better for wherever its being used
            if (!hasValueOrZero(initialAmount) && !initialCurrency) {
                return null;  // if both are missing/invalid, return null
            }
            return { amount: initialAmount, currency: initialCurrency };
            // console.log('running money parser on value:', value, 'decimalPlaces:', decimalPlaces);
            // const { amount, currency } = value;
            // const amt = trimString(amount);
            // console.log('parsing money value, amt:', amt, 'currency:', currency, 'currencyCodeOptions:', currencyCodeOptions);
            // // NOTE: our with/without decimalPlaces parsing is not currently the same (one can take strings or numbers, other can't I think...)
            // // const parsedAmount = isNil(decimalPlaces) ? parseDecimal(amt) : parseFixedDecimal(amt, decimalPlaces);
            // const parsedAmount = parseFixedDecimal(amt, decimalPlaces);  // always parse to fixed decimal places for money
            // const parsedCurrency = isString(currency) && currencyCodeOptions?.includes(currency) ? currency : (currencyCodeOptions?.length === 1 ? currencyCodeOptions[0] : '');
            // // return { amount: parsedAmount, currency: parsedCurrency };  // return object no matter what?  or just null?
            // if (!hasValueOrZero(parsedAmount) || !parsedCurrency) {
            //     return null;
            // }
            // return { amount: parsedAmount, currency: parsedCurrency };
        };
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        // NOTE: value will be (SHOULD BE) an object with { amount, currency } or null/undefined -- validate that here too
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessageIfRuleIsFalse]
        // validate the type first -- either null, undefined, or object with amount and currency -- nothing else -- and we'll validate amount/currency separately in another rule
        rules.push([(value) => {
            if (isNil(value)) {
                return true;  // allow null/undefined here... let required rule handle that if needed
            }
            if (typeof value === 'object' && value && value.amount !== undefined && value.currency !== undefined) {
                // make sure we have an object with amount and currency keys
                return true;
            }
            return false;  // otherwise false/fail
        }, 'Invalid money value (wrong type)']);
        // validate that we have both amount and currency if we have one of them
        rules.push([(value) => {
            // this rule itself is forgiving on types, since we have separate rule for that
            if (isNil(value)) {
                return true;  // allow null/undefined here... let required rule handle that if needed
            }
            const { amount, currency } = value || {};  // this is the forgiving part
            const hasAmount = hasValueOrZero(amount);
            const hasCurrency = !!currency;
            if (hasAmount && hasCurrency) {  // have both, so valid
                return true;
            }
            if (!hasAmount && !hasCurrency) {  // have neither, so valid if not required -- we let the required rule handle that
                return true;
            }
            return false;  // otherwise invalid
        }, 'Missing amount or currency for money value']);
        // now validate min/max on amount only -- we are forgiving on the presence of amount here... let other validation rules handle that
        if (colSchema.min) {
            rules.push([(value) => {
                const amount = value?.amount;
                if (!hasValueOrZero(amount)) {
                    return true;  // skip validation if no amount... let other rules handle requiredness
                }
                return amount >= colSchema.min;
            }, `Min value is ${colSchema.min}`]);
        }
        if (colSchema.max) {
            rules.push([(value) => {
                const amount = value?.amount;
                if (!hasValueOrZero(amount)) {
                    return true;  // skip validation if no amount... let other rules handle requiredness
                }
                return amount <= colSchema.max;
            }, `Max value is ${colSchema.max}`]);
        }
        if (colSchema.required) {
            rules.push([(value) => {
                const amount = value?.amount;
                return hasValueOrZero(amount);
            }, `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const decimalPlaces = getDecimalPlacesFromSchema(colSchema);
        const defaults = {
            cellEditor: 'moneyCellEditor',
            cellEditorParams: {
                decimalPlaces: decimalPlaces,
                currencyOptions: getCurrencyOptionsFromSchema(colSchema),
                //useFormatter: true,  // be careful about using formatter in editor... it can blank values if not careful
            },
            headerClass: ['column-align-right'],
            cellClass: ['column-align-right'],
            // THESE cellClassRules ARE FINE, but we added this logic to the validation checker, and will show any invalid cells with red text anyways
            // cellClassRules: {  // need to show invalid money data in red -- like amounts without currency, or vice versa -- maybe use custom renderer instead so you can show partial data?
            //     'text-danger': (params) => {
            //         const value = params.value;  // will be null, or object with { amount, currency }, or maybe undefined?
            //         if (!value || typeof value !== 'object') {
            //             return false;
            //         }
            //         const { amount, currency } = value;
            //         const hasAmount = hasValueOrZero(amount);
            //         const hasCurrency = !!currency;
            //         if (hasAmount && hasCurrency) {  // have both, so valid
            //             return false;
            //         }
            //         if (!hasAmount && !hasCurrency) {  // have neither, so valid if not required -- we let the required rule handle that
            //             return false;
            //         }
            //         return true;
            //     },
            // },
            filter: false,  // no filter for money type because of confusion about currency?... could do it for fixed currency only?
            sortable: false,
            // minWidth: 140,  // we can't cutoff the currency select box or the number input... those are minWidth of 60 + 80 basically, so 140 is minimum here
            // width: 140,  // this is the initial width, but can be resized
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // OLD -- showed empty string for anything invalid: colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueFormatter'] = (params) => {
            // this should be similar to our formatter, but shows warnings if amount or currency is missing (only for inside the grid)
            const value = params.value;
            const { amount, currency } = value || {};
            const hasAmount = hasValueOrZero(amount);
            const hasCurrency = !!currency;
            if (!hasAmount && !hasCurrency) {
                return '';  // both missing, so show empty string
            }
            if (hasAmount && !hasCurrency) {  // amount without currency
                return `Missing Currency`;  // show warning if one is missing
            }
            if (!hasAmount && hasCurrency) {  // currency without amount -- probably won't happen due to our cellEditor rejecting stuff without amount, but still
                return `Missing Amount`;  // show warning if one is missing
            }
            return formatMoney(amount, currency, decimalPlaces);
        },
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));
        // add/override some things based on colSchema?
        // const decimalPlaces = getDecimalPlacesFromSchema(colSchema);
        // colDef['cellRenderer'] = (params) => {
        //     const value = params.value || {};
        //     console.log('running money RENDERER on value:', value, 'decimalPlaces:', decimalPlaces);
        //     const { amount, currency } = value;
        //     if (!hasValueOrZero(amount) || !currency) {
        //         return '';
        //     }
        //     // seems AG-Grid will always output [object Object] since the internal value will be an object.... so we need to use custom renderer?
        //     return formatMoney(amount, currency, decimalPlaces);
        // };
        return colDef;
    },
};


const dateType = {  // we use JS Date internally in the grid/data since ag-grid has better support for JS Date objects (in filters specifically) - but then we need to convert to string to-from backend
    typeKey: 'date',
    label: 'Date',
    defaultValue: null,
    getFormatter: () => {
        return (value) => formatDate(value);
    },
    getParser: () => {
        // pass in something about the timezone to use????
        // parse to JS Date -- that's what AG-Grid wants
        return (value) => {
            const luxonDate = toLuxonDate(value);
            if (!luxonDate?.isValid) {
                return null;
            }
            return toJSDate(luxonDate);
        };
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage] - if func returns false, show the error message
        if (colSchema.min) {
            const minJSDate = toJSDate(colSchema.min);
            rules.push([(value) => value >= minJSDate, `Min value is ${minJSDate}`]);
        }
        if (colSchema.max) {
            const maxJSDate = toJSDate(colSchema.max);
            rules.push([(value) => value <= maxJSDate, `Max value is ${maxJSDate}`]);
        }
        if (colSchema.required) {
            rules.push([(value) => !!value, `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const defaults = {
            cellEditor: 'bootstrapVueDatePickerEditor', 
            // filter: false,  // no filter for money type because of confusion about currency?... could do it for fixed currency only?
            // sortable: false,
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // add/override some things based on colSchema?
        colDef['valueFormatter'] = makeAgGridFormatter(this.getFormatter(colSchema));
        colDef['valueParser'] = makeAgGridParser(this.getParser(colSchema));
        return colDef;
    },
    getExcelPrepFn: () => {  // can optionally take colSchema if needed
        // return function to prep value for excel export (this is a closure)
        // Convert JS Date (or some empty value) to formatted string for excel
        return (value) => formatDate(value);
    },
    getBackendPrepFn: () => {  // can optionally take colSchema if needed
        // return function to prep value for backend  (this is a closure)
        // Convert JS Date (or some empty value) to ISO string for backend
        return (value) => {
            if (!value) {
                // normalized empty things to null for consistency
                return null;
            }
            return toISODate(value);
        };
    },
};

const getOptionsObjectFromSchema = (colSchema) => {
    // returns object of {value: label} for use as refData in ag-grid colDef
    if (colSchema.refData) {
        return colSchema.refData;
    }
    let options = {};
    const valueKey = colSchema.options_value_key || 'value';
    const labelKey = colSchema.options_label_key || 'label';
    // TODO - what about the disabled key?  just remove disabled ones from the options completely?  or will that cause problems with existing values?
    if (Array.isArray(colSchema.options)) {
        options = {};
        const sortedOptions = orderBy(
            colSchema.options,
            [(opt) => (opt?.[labelKey] ?? '').toString().toLowerCase()],
            ['asc'],
        );
        for (const opt of sortedOptions) {
            options[opt[valueKey]] = opt[labelKey];
        }
    }
    return options;
};


const customSelectCellRenderer = (params) => {
    // custom function-based cell renderer for single_select type that we use to customize a few thigns like empty value display
    const placeholderText = 'Select...';
    if (isNil(params.value) || params.value === '') {
        return `<span class="text-gray-400 font-italic">${placeholderText}</span>`;  // show placeholder text in muted color for empty values
    }
    // you could also return a <span> or other HTML here
    return params.valueFormatted || params.value;  // use formatted value if available, otherwise raw value
}

const singleSelectType = {
    typeKey: 'single_select',
    label: 'Single Select',
    defaultValue: null,
    // formatter / parser not needed if you have refData in colDef???  but then you can't use this for non-ag-grid stuff???
    getFormatter: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (value) => optionsObj[value] || value;
    },
    // getFormatter: (colSchema) => {  // using custom renderer instead...
    //     const placeholderText = '-- Select --';
    //     const optionsObj = getOptionsObjectFromSchema(colSchema);
    //     return (value) => {
    //         if (isNil(value) || value === '') {
    //             return placeholderText;  // TODO - this does not work... maybe it has its own way of doing this...
    //         }
    //         return optionsObj[value] || value;
    //     }
    // },
    getParser: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (value) => {
            // value here is the raw value coming in... could be the key of an option (check that first), otherwise the LABEL (if coming from Excel, for example)
            if (Object.keys(optionsObj).includes(value)) {
                return value;  // it's a valid key, so return it
            } else if (isNil(value) || value === '') {
                return null;  // treat empty/blank as null
            } else {
                // try the value as a label instead of a key (for example, if coming from Excel, it might be the label instead of the key)
                const foundPair = Object.entries(optionsObj).find((keyLabelPair) => keyLabelPair[1].toString().toLowerCase() === value.toString().toLowerCase());
                if (foundPair) {
                    return foundPair[0];  // return the key if we found a matching label
                }
            }
            return null;  // if we didn't find a valid key or label, return null to indicate invalid value
            // return Object.keys(optionsObj).includes(value) ? value : null;
        };
    },
    getExcelPrepFn: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (value) => optionsObj[value] || value;
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        if (optionsObj) {
            const validValues = Object.keys(optionsObj);
            rules.push([(value) => isNil(value) || validValues.includes(value), `Value is not a valid option: valid options are: ${validValues.join(', ')}`]);
        }
        if (colSchema.required) {
            const validValues = Object.keys(optionsObj);
            rules.push([(value) => validValues.includes(value), `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        // handle stuff like options url, etc.
        const defaults = {
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
                values: Object.keys(optionsObj),
                cellRenderer: customSelectCellRenderer,
            },
            refData: optionsObj,
            cellRenderer: customSelectCellRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: Object.keys(optionsObj),
                cellRenderer: customSelectCellRenderer,
            },
            sortable: true,
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // do dynamic things based on options related things (url, endpoint, etc.)
        // NOT adding formatter/parser because we are using refData
        return colDef;
    },
};

const MULTI_SELECT_EXCEL_SEPARATOR = '|';  // we will use this to join/split multi select values for Excel export/import
const MULTI_SELECT_CELL_RENDERER_SEPARATOR = ', ';  // this is just for display inside the grid -- we can use a more user-friendly separator here since we don't have to worry about parsing it back -- we just need to make sure it's not in the actual values too much -- comma is usually pretty safe for this, but you can change if needed
// we export to Excel joining on this separator, and then in the parser we will split on this separator
// we want to use a separator that is not likely to be in the actual values, but also something that Excel won't mess with too much
// pipe is usually a good choice for this

const customMultiSelectCellRenderer = (params) => {
    // custom function-based cell renderer for multi_select type that we use to customize a few things like empty value display
    const placeholderText = 'Select...';
    if (isNil(params.value) || params.value === '' || (Array.isArray(params.value) && params.value.length === 0)) {
        return `<span class="text-muted font-italic">${placeholderText}</span>`;  // show placeholder text in muted color for empty values
    }
    // you could also return a <span> or other HTML here
    if (!Array.isArray(params.value)) {
        return '';
    }
    // TODO - maybe we should put this logic in the cellFormatter, then just use return params.valueFormatted || params.value here?  but then we have to make sure to use the formatter for excel export and other places too... maybe it's better to just have it all in the renderer since we have this special logic for empty values and joining arrays, etc. that we don't want to forget about in other places?
    const optionsObj = params.optionsObject || {};
    return params.value.map((value) => optionsObj[value] || value).join(MULTI_SELECT_CELL_RENDERER_SEPARATOR);
    // return params.valueFormatted || params.value;  // use formatted value if available, otherwise raw value
}

const cleanMultiSelectValue = (rawValue, optionsObject) => {
    // this is a helper function to clean/normalize multi select values coming in from various sources before we do other things with it
    // you can pass in the raw value (which could be in various formats, such as an array of keys, a delimiter-separated string of keys, an array of labels, etc.) and the options object (mapping keys to labels), and it will return a cleaned array of valid keys
    console.log('cleanMultiSelectValue called with rawValue:', rawValue, 'optionsObject:', optionsObject);
    const optionsPairs = Object.entries(optionsObject);
    const optionsKeys = Object.keys(optionsObject);
    const optionsLabels = Object.values(optionsObject);
    // NOTE - in the next line, we might have a paste from Excel OR from another cell -- so either delimiters could be used
    // we will use some additional heuristics to try to determine the format of the incoming value -- if it's an array, we will assume it's an array of keys or labels; if it's a string, we will try parsing as one of delimiter-separated values, will try both
    let valuesArray = [];
    if (Array.isArray(rawValue)) {
        valuesArray = rawValue;  // assume it's an array of keys OR labels
    } else if (typeof rawValue === 'string') {
        // try splitting on both delimiters and see if we get valid keys or labels -- if we get valid keys or labels from one of the splits, use that, otherwise treat the whole string as a single value to check
        const splitByExcelSeparator = rawValue.split(MULTI_SELECT_EXCEL_SEPARATOR).map(v => v.trim()).filter(v => v !== '');
        const splitByCellRendererSeparator = rawValue.split(MULTI_SELECT_CELL_RENDERER_SEPARATOR).map(v => v.trim()).filter(v => v !== '');
        if (isSuperset(optionsKeys, splitByExcelSeparator) || isSuperset(optionsLabels, splitByExcelSeparator)) {
            // if the split values are all valid keys or labels, then we will assume this is the correct split to use -- this allows us to handle both cases where we might be getting a string from Excel (using the Excel separator) or from another cell (using the cell renderer separator)
            valuesArray = splitByExcelSeparator;
        } else if (isSuperset(optionsKeys, splitByCellRendererSeparator) || isSuperset(optionsLabels, splitByCellRendererSeparator)) {
            // if the split values are all valid keys or labels, then we will assume this is the correct split to use -- this allows us to handle both cases where we might be getting a string from Excel (using the Excel separator) or from another cell (using the cell renderer separator)
            valuesArray = splitByCellRendererSeparator;
        } else if (isSuperset(optionsKeys, [rawValue]) || isSuperset(optionsLabels, [rawValue])) {
            valuesArray = [rawValue];  // treat as single value to check
        } else {
            valuesArray = [];  // no valid keys or labels found, so we don't have any valid values to return?
        }
    }
    // const valuesArray = Array.isArray(rawValue) ? rawValue : (typeof rawValue === 'string' ? rawValue.split(MULTI_SELECT_EXCEL_SEPARATOR).map(v => v.trim()).filter(v => v !== '') : [rawValue]);
    const parsedValue = [];
    valuesArray.forEach((value) => {
        if (Object.keys(optionsObject).includes(value)) {
            parsedValue.push(value);  // it's a valid key, so add it to our parsed values
        } else if (isNil(value) || value === '') {
            // return null;  // treat empty/blank as null, not real values, so skip them
        } else {
            // try the value as a label instead of a key (for example, if coming from Excel, it might be the label instead of the key)
            // we can't make a lookup of labels because they might not be unique, so we have to search through the options to find a matching label (case-insensitive) -- we will take the first match if we find multiple, but ideally labels should be unique for this reason
            const foundPair = optionsPairs.find((keyLabelPair) => keyLabelPair[1].toString().toLowerCase() === value.toString().toLowerCase());
            if (foundPair) {
                parsedValue.push(foundPair[0]);  // return the key if we found a matching label
            }
        }
        // if we didn't find a valid key or label, nothing to add to parsed values
    });
    return parsedValue;
};

const multiSelectType = {
    typeKey: 'multi_select',
    label: 'Multi Select',
    defaultValue: () => [],  // yes, they can be callables, which is important here
    // formatter / parser not needed if you have refData in colDef???  but then you can't use this for non-ag-grid stuff???
    getFormatter: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (rawValue) => {
            if (!Array.isArray(rawValue)) {
                return '';
            }
            return rawValue.map((value) => optionsObj[value] || value).join(MULTI_SELECT_CELL_RENDERER_SEPARATOR);
        };
    },
    getParser: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        // const optionsPairs = Object.entries(optionsObj);
        return (rawValue) => {
            // value here is the raw value coming in... could be the key of a SINGLE option, or single LABEL (if coming from Excel, for example)
            //    or arrays of them!  or comma-separated strings of them???
            return cleanMultiSelectValue(rawValue, optionsObj);
        };
    },
    getExcelPrepFn: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (rawValue) => {
            // value here is an array of selected keys, we want to convert to labels, then join on our separator for Excel export
            if (!Array.isArray(rawValue)) {
                return '';
            }
            return rawValue.map((value) => optionsObj[value] || value).join(MULTI_SELECT_EXCEL_SEPARATOR);
        };
    },
    getRules: (colSchema) => {  // OR JUST getRules()?
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        if (optionsObj) {
            const validValues = Object.keys(optionsObj);
            // this one should NOT enforce being required -- that is next - we allow null/undefined/empty array if not required, but if there are values, they must be valid options
            rules.push([(value) => isNil(value) || (Array.isArray(value) && value.length === 0) || (Array.isArray(value) && value.every(v => validValues.includes(v))), `Value does not consist of value options - valid options are: ${validValues.join(', ')}`]);
        }
        if (optionsObj && colSchema.required) {
            // const validValues = Object.keys(optionsObj);
            // if here we clean the value, then make sure we have at least one valid value -- the other rule will check that all values are valid if present
            rules.push([(value) => !isNil(value) && cleanMultiSelectValue(value, optionsObj).length > 0, `This field is required`]);
        }
        return rules;
    },
    getBaseColDef: function (colSchema) {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        // handle stuff like options url, etc.
        const defaults = {
            cellEditor: 'multiSelectCellEditor', 
            cellEditorParams: {
                optionsObject: optionsObj,  // our editor expects this
                // values: Object.keys(optionsObj),
                // cellRenderer: customSelectCellRenderer,  // TODO - likely need to change this...
            },
            // refData: optionsObj,
            cellRenderer: customMultiSelectCellRenderer,
            cellRendererParams: {
                optionsObject: optionsObj,  // pass this down to renderer too in case it needs to display labels instead of keys
            },
            // flex: 2, // flex is not very useful for us since we update widths via API so often, which cancels flex behavior
            wrapText: true,  // things will likely get long with multiple values, so we allow wrapping
            autoHeight: true,  // need this for wrapText
            filter: false,  // just turn off filters for now
            // filter: 'agSetColumnFilter',
            // filterParams: {
            //     values: Object.keys(optionsObj),
            //     cellRenderer: customSelectCellRenderer,  // single-select renderer???
            // },
            sortable: false,  // not sortable because of complexity of multi-select values?  
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // do dynamic things based on options related things (url, endpoint, etc.)
        // NOT adding formatter/parser because we are using refData
        return colDef;
    },
};


const s3FilesType = {
    typeKey: 's3_files',
    label: 'Files',
    defaultValue: () => [],  // even for single type????
    getFormatter: (colSchema) => {
        // convert to something like "filename1 (url1), filename2 (url2)" for multiple files, or just "filename (url)" for single file
        const { single } = colSchema.props || {};
        return (value) => {
            if (!value) {
                return '';
            }
            if (single === true) {
                // single file mode
                const { filename } = value || {};
                return filename || '';
            }
            // multiple files mode
            return value.map(({ filename }) => filename).join(', ');
        };
    },
    getParser: (colSchema) => {
        const optionsObj = getOptionsObjectFromSchema(colSchema);
        return (value) => {
            return Object.keys(optionsObj).includes(value) ? value : null;
        };
    },
    // TODO - make sure files are not copy/pastable -- or else you'll have multiple references to same file, which is not really supported
    getRules: (colSchema) => {  // OR JUST getRules()?
        // return a function that takes value and returns an Array of error strings (empty array if no errors)
        const rules = [];  // 2 element arrays of [ruleFunc, errorMessage]
        if (colSchema.required) {
            // REMEMBER - value here is an array of objects (or could be null/undefined)
            rules.push([(value) => !!value && Array.isArray(value) && value.length > 0, `This field is required`]);
        }
        return rules;
    },
    getExcelPrepFn: (colSchema) => {
        // return function to prep value for excel export (this is a closure)
        // convert to something like "filename1 (url1), filename2 (url2)" for multiple files, or just "filename (url)" for single file
        const { single } = colSchema.props || {};
        return (value) => {
            if (!value) {
                return '';
            }
            if (single === true) {
                // single file mode
                const { filename } = value || {};
                return `${filename}` || '';
            }
            // multiple files mode
            return value.map(({ filename }) => `${filename}`).join(', ');
        };
    },
    getBaseColDef: function (colSchema) {
        const props = colSchema.props || {};
        const { parent_id, file_category } = colSchema || {};  // these are NOT in props, but at top-level of colSchema
        // get things like allowed file types, max files, max size, etc. from props???  (because that's where we put them for formulate renderer)
        // we got these directly from formulate renderer props
        // const { maxItems, single, accept, imageOnly, maxFilesize, maxTotalFilesize, hideLimits, ...otherProps } = props;
        const fileInputProps = {fileCategory: file_category, parentId: parent_id, ...props};  // we merge the top-level ones in too, for convenience, and put them all in a nested object to avoid collisions with built-in ag-grid things
        const componentParams = {
            fileInputProps: fileInputProps,  // nest them all one layer down, so we don't collide with other things!
            // fileCategory: file_category || '',
            // parentId: parent_id || null,  // null is allowed/used in some cases
            // single: single === true,  // default to false
            // accept: accept || '',  // should be string...
            // imageOnly: imageOnly === true,
            // hideLimits: hideLimits === true,
            // maxItems: isNumber(maxItems) ? maxItems : 1,  // default to 1?
            // maxFilesize: isNumber(maxFilesize) ? maxFilesize : null,  // in MB???
            // maxTotalFilesize: isNumber(maxTotalFilesize) ? maxTotalFilesize : null,  // in MB???
            // ...otherProps,  // in case we add more in future
            // allowedFileTypes: Array.isArray(props.allowed_file_types) ? props.allowed_file_types : [],
        };
        const defaults = {
            cellEditor: 'agGridFileEditor',
            cellEditorParams: {...componentParams},
            cellRenderer: 'agGridFileRenderer',
            cellRendererParams: {...componentParams},  // we pass them all down, but renderer may not need/use them all
            filter: false,
            sortable: false,
            autoHeight: true,  // should make row height auto-adjust to text?
            suppressPaste: true,  // do not allow paste into file columns
            minWidth: 120,
            width: 120,  // initial width, but can be resized
            // other defaults?
        };
        const colDef = {...defaults, ...colSchema.getColDefData()};
        // do dynamic things based on options related things (url, endpoint, etc.)
        // no formatter/parser needed because we are using custom renderer/editor
        return colDef;
    },
};

const rendererKeyToColType = {
    hidden: hiddenType,  // special type that just hides the column, doesn't set/get?
    string: textType,
    textarea: textType, // we have special handling for this in getBaseColDef of textType
    boolean: booleanType,
    number: decimalType,
    integer: integerType,
    float: decimalType,
    decimal: decimalType,
    money: moneyType,
    date: dateType,
    advancedselect: singleSelectType,
    select: singleSelectType,  // TODO - need to handle MULTIPLE select separately?
    s3_files: s3FilesType,
};
// other renderer_key values:
// hidden 
// textarea
// number  (data_type on these is "str", but that's the case for many of them...)

const getColTypeForColSchema = (colSchema) => {
    const selectRendererKeys = ['advancedselect', 'select'];
    const rendererKey = colSchema.renderer_key || 'string';  // default to string type if no renderer_key specified
    if (selectRendererKeys.includes(rendererKey)) {
        // need to check if it's single or multi select based on presence of "multiple" in props
        const isMultiSelect = colSchema.multiple === true;  // default to false if not present
        return isMultiSelect ? multiSelectType : rendererKeyToColType[rendererKey];
    }
    return rendererKeyToColType[rendererKey] || textType;  // default to textType if unknown rendererKey
};




class BackendFlowFormItem {
    // constructor({ field, headerName, rendererKey, editable, type, ...other }) {
    constructor(backendFormItemKwargs = {}) {
        // just pass everything through to this object directly from backend, so schema is same...
        for (let key in backendFormItemKwargs) {
            this[key] = backendFormItemKwargs[key];
        }
        // const kwargs = backendFormItemKwargs;
        // const { name, label, renderer_key, read_only, ...other } = kwargs;
        // this.field = name;
        // this.headerName = label;
        this.rendererKey = backendFormItemKwargs.renderer_key || 'string';
        // DEFAULT VALUE - we may have to parse them based on type???
        // so maybe this should be something on the colType???  yes...
        // NOTE: in backend, we consider None (python None) to be "no default set" - so we do the same here (undefined or null, we don't set a default)
        if (!isNil(backendFormItemKwargs.default_value)) {
            this.defaultValueRaw = backendFormItemKwargs.default_value;
        }
        // this.editable = !read_only;
        // // this.spl_type = kwargs.type;  // not passing to type yet....
        // // add other attrs passed in
        // this.extra = {};
        // for (let key in other) {
        //     this.extra[key] = other[key];
        //     this[key] = other[key];
        // }
    }
    getColDefData() {
        const colDef = {
            field: this.name,
            headerName: this.label || this.name,
            headerTooltip: this.description || null,
            editable: !this.read_only,
            // ...this.extra,  // no, we don't want to pass it through here... will have tons of extra stuff
        };
        const hideRendererKeys = ['hidden'];  // add any other unsupported ones here so they just get hidden?  or we should actually remove?
        if (hideRendererKeys.includes(this.rendererKey)) {
            colDef['hide'] = true;
        }
        // if (this.default_value !== undefined) {
        //     colDef['defaultValue'] = this.default_value;
        // }
        // options and refData handled in colType
        // cellEditor, filter, etc. handled in colType
        return colDef;
    }
}


class LiveGridCol {
    constructor(backendFormItemKwargs) {
        this.field = backendFormItemKwargs.name;  // then key by these and add to gridContext
        this.colSchema = new BackendFlowFormItem(backendFormItemKwargs);
        // this.colType = rendererKeyToColType[this.colSchema.rendererKey] || textType;
        this.colType = getColTypeForColSchema(this.colSchema);
        // = colType ? colType : textType;  // default to text type if unknown rendererKey
        // pass in some reference to the form?  form data?
        this.colTypeKey = this.colType.typeKey;  // shortcut to colType.typeKey for easy access
    }
    // METHODS TO GET THINGS FOR AG-GRID COLDEF and other things
    getColDef(overrides = {}) {
        const baseColDef = this.colType.getBaseColDef(this.colSchema);
        const colDef = {...baseColDef, ...overrides};  // overrides win, only shallow merged
        // const colDef = cloneDeep(this.colSchema);
        // TODO - do not hadd formatter and parser here, since those are generic things, and sometimes ag-grid handles them its own way (like for refData)
        // const formatter = this.colType?.getFormatter ? this.colType?.getFormatter(this.colSchema) : null;
        // if (formatter) {
        //     colDef['valueFormatter'] = (params) => {
        //         // console.log(`running formatter for field ${this.field} on value:`, params.value, params);
        //         const formattedValue = formatter(params.value);
        //         // console.log(`formatted value is:`, formattedValue);
        //         return formattedValue;
        //     };
        // }
        // const parser = this.colType?.getParser ? this.colType?.getParser(this.colSchema) : null;
        // if (parser) {
        //     colDef['valueParser'] = (params) => {
        //         // console.log(`running parser for field ${this.field} on newValue:`, params.newValue, params);
        //         const parsedValue = parser(params.newValue);
        //         // console.log(`parsed value is:`, parsedValue);
        //         return parsedValue;
        //     };
        // }
        // colDef['valueFormatter'] = (params) => {
        //     const val = params.value;  // this is shitty... improve... just make the whole colDef in getBaseColDef???  or allow passing params to the functions you make?  but then you can't use outside of ag-grid...
        //     const formatter = this.colType?.getFormatter(this.colSchema);
        //     return formatter ? formatter(val) : val;
        // };
        // colDef['valueParser'] = (params) => {
        //     const val = params.newValue;
        //     const parser = this.colType?.getParser(this.colSchema);
        //     return parser ? parser(val) : val;
        // };
        console.log('getColDef', colDef);
        return colDef;
    }
    getDefaultValue() {
        // we first check the colSchema for defaultValueRaw
        // if that is undefined, we return any defaultValue from the colType
        // in either case, we parse the value before returning it
        if (this.colSchema.defaultValueRaw !== undefined) {
            return this.parseValue(this.colSchema.defaultValueRaw);
        }
        if (this.colType.defaultValue !== undefined) {
            if (isFunction(this.colType.defaultValue)) {
                return this.parseValue(this.colType.defaultValue());
            }
            return this.parseValue(this.colType.defaultValue);
        }
        // if no default specified anywhere, return undefined meaning 'has no default'
        return undefined;
    }
    formatValue(value) {
        const formatter = this.colType?.getFormatter ? this.colType?.getFormatter(this.colSchema) : null;
        return formatter ? formatter(value) : value;
    }
    parseValue(value) {
        const parser = this.colType?.getParser ? this.colType?.getParser(this.colSchema) : null;
        return parser ? parser(value) : value;
    }
    getErrors(value) {
        const rules = this.colType?.getRules ? this.colType?.getRules(this.colSchema) : null;
        // console.log(`getErrors for field: ${this.field} (type: ${this.colTypeKey}) - value:`, value, 'NUMBER OF RULES:', Array.isArray(rules) ? rules.length : 0, 'rules:', rules);
        return rules ? getErrorsFromRules(rules, value) : [];
    }
    prepValueForExcel(value) {  // TODO - implement this on colTypes that need it...
        // if colType has prepValueForExcel method, use it, otherwise use formatValue
        const prepper = this.colType?.getExcelPrepFn ? this.colType?.getExcelPrepFn(this.colSchema) : null;
        return prepper ? prepper(value) : this.formatValue(value);
    }
    prepValueForBackend(value) {
        // if colType has prepValueForBackend method, use it, otherwise just return value
        // use this to "normalize" empty values also... ie: "" -> null for numbers, dates, etc.
        const prepper = this.colType?.getBackendPrepFn ? this.colType?.getBackendPrepFn(this.colSchema) : null;
        return prepper ? prepper(value) : value;
    }
    // TYPE-SPECIFIC METHODS???  this is why you need to make subclasses of a base class...
    getDecimalPlaces() {
        return getDecimalPlacesFromSchema(this.colSchema);  // returns null if none specified (or wrong col type)
    }
    getCurrencyOptions() {
        return getCurrencyOptionsFromSchema(this.colSchema);  // returns array of currency codes, or null if none specified (or wrong col type)
    }
}


export {
    LiveGridCol,
}